import { motion } from 'framer-motion';
import { Coffee, ExternalLink } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { SEO } from '../components/SEO';
import { VisitUs } from '../components/VisitUs';
const ORDER_URL = 'https://order.dripos.com/Grounded-Livermore';
type MenuItem = {
  name: string;
  description: string;
};
type MenuSection = {
  id: string;
  title: string;
  note?: string;
  items: MenuItem[];
};
const menuSections: MenuSection[] = [{
  id: 'coffee',
  title: 'Coffee',
  note: 'Proyecto Diaz on drip, with rotating pour-overs from partner roasters.',
  items: [{
    name: 'Drip Coffee',
    description: 'Bright, balanced medium-light roast from Proyecto Diaz.'
  }, {
    name: 'Cold Brew',
    description: 'Chromatic dark roast, slow-steeped for a smooth cup with chocolate notes.'
  }, {
    name: 'Vietnamese Cold Brew',
    description: 'Butter-roasted robusta poured over sweetened condensed milk.'
  }, {
    name: 'Pour-Over',
    description: 'Brewed to order from our rotating partner roasters.'
  }, {
    name: 'Cafe au Lait',
    description: 'Drip coffee softened with velvety steamed milk.'
  }, {
    name: 'Batch Coffee (to-go)',
    description: 'One gallon, serves about 10. Decaf available — perfect for the office or a team meeting.'
  }]
}, {
  id: 'espresso',
  title: 'Espresso',
  note: "Pulled with Chromatic's Gamut roast — rich chocolate and nutty notes.",
  items: [{
    name: 'Latte',
    description: 'Espresso with steamed milk and a thin cap of foam.'
  }, {
    name: 'Cappuccino',
    description: 'Equal parts espresso, steamed milk, and airy foam.'
  }, {
    name: 'Flat White',
    description: 'Ristretto-forward and silky, with a thin layer of microfoam.'
  }, {
    name: 'Cortado',
    description: 'Espresso cut with an equal pour of warm milk.'
  }, {
    name: 'Macchiato',
    description: 'Espresso marked with a spoonful of dense foam.'
  }, {
    name: 'Americano',
    description: 'Espresso lengthened with hot water. Also served iced.'
  }, {
    name: 'Mocha',
    description: 'Espresso and chocolate under steamed milk.'
  }, {
    name: 'Espresso',
    description: 'A straight shot, served as a single or double.'
  }, {
    name: 'Espresso + Tonic',
    description: 'Espresso over tonic and ice — bright, bubbly, and refreshing.'
  }]
}, {
  id: 'non-coffee-tea',
  title: 'Non-Coffee & Tea',
  items: [{
    name: 'Chai Latte',
    description: 'David Rio and Chai Cart masala blended with steamed milk.'
  }, {
    name: 'Matcha Latte',
    description: 'Whisked green tea with your choice of milk.'
  }, {
    name: 'Hojicha Latte',
    description: 'Roasted green tea — nutty, toasty, and low in caffeine.'
  }, {
    name: 'London Fog',
    description: 'Earl Grey with steamed milk and a touch of vanilla.'
  }, {
    name: 'Golden Latte',
    description: 'Turmeric and warm spices in steamed milk.'
  }, {
    name: 'Hot & Iced Tea',
    description: 'Far Leaves loose leaf, brewed by the cup.'
  }, {
    name: 'Italian Soda',
    description: 'Sparkling water and flavored syrup over ice.'
  }, {
    name: 'Lavender Lemonade',
    description: 'House lemonade with floral lavender.'
  }, {
    name: 'Strawberry Lemonade',
    description: 'House lemonade with sweet strawberry.'
  }, {
    name: 'Hot Chocolate',
    description: 'Rich chocolate and steamed milk.'
  }, {
    name: 'Steamer',
    description: 'Steamed milk with the flavor of your choice. Kid-friendly.'
  }]
}, {
  id: 'food',
  title: 'Made-to-Order Food',
  note: 'Built when you order it, so give us a few minutes.',
  items: [{
    name: 'Breakfast Sando',
    description: 'Bacon, egg, and cheese on ciabatta with Kewpie mayo and Yellowbird sriracha.'
  }, {
    name: 'Kimchi Breakfast Sando',
    description: 'Bacon, egg, scallion, cheese, and kimchi with chili-oil aioli.'
  }, {
    name: 'Smoked Salmon & Labne',
    description: 'Smoked salmon, Karoun labne, spring mix, and pickled onion on a croissant.'
  }, {
    name: 'Premier Salami Sando',
    description: 'Salami, turkey, and cheddar on Dutch crunch.'
  }, {
    name: 'Pastrami & Havarti',
    description: 'Beef pastrami, havarti, and dill pickle on Dutch crunch.'
  }, {
    name: 'The Grounded Italian',
    description: 'Prosciutto, mortadella, salami, havarti, and the works.'
  }, {
    name: 'Turkey, Avocado & Havarti',
    description: 'Stacked on sourdough.'
  }, {
    name: 'Very Veggie',
    description: 'Cheddar, havarti, veggies, and pickles on sourdough.'
  }, {
    name: 'Grounded Avocado Toast',
    description: 'Sourdough with avocado, bacon, and an over-easy egg.'
  }, {
    name: 'Burrata Toast',
    description: 'Sundried tomato, pesto, marinated tomatoes, and burrata.'
  }, {
    name: 'Savory Oats',
    description: 'Oats cooked in chicken broth with bacon, egg, kimchi, and scallion.'
  }, {
    name: 'Tomato & Burrata Arugula Salad',
    description: 'Fresh burrata and tomatoes over peppery arugula.'
  }, {
    name: 'Yogurt Parfait',
    description: 'Yogurt, honey, granola, and seasonal berries.'
  }, {
    name: 'Nutella + Strawberry Toast',
    description: 'Nutella and fresh strawberries on toast.'
  }]
}, {
  id: 'pastries',
  title: 'Pastries & Sweets',
  items: [{
    name: 'The Groundbreaker Cookie',
    description: 'Brown butter, corn flakes, and chocolate chip. Our signature.'
  }, {
    name: 'Chocolate Croissant',
    description: 'Flaky, buttery, and filled with dark chocolate.'
  }, {
    name: 'Plain Croissant',
    description: 'All butter, all layers.'
  }, {
    name: 'Muffins',
    description: 'Baked fresh, flavors rotate.'
  }, {
    name: 'Cake Pops',
    description: 'A small sweet bite for the road.'
  }, {
    name: 'PB&J with Fresh Strawberries',
    description: 'Peanut butter and jam with sliced fresh strawberries.'
  }]
}, {
  id: 'retail',
  title: 'Retail & Local Goods',
  note: 'Available in-store.',
  items: [{
    name: 'Coffee Beans & Gear',
    description: 'Whole bean from Temple Coffee, Proyecto Diaz, and Chromatic, plus Fellow brewing equipment.'
  }, {
    name: 'Snacks & Pantry',
    description: 'Butter & Batter cookies, Ginger Lab ginger beer, Yellowbird hot sauce, JTM seasonings, Momo ceremonial matcha, Yamabuki hojicha, and Grounded tote bags.'
  }, {
    name: "Neighbor's Cup",
    description: 'Pay it forward — buy a coffee for a member of the community.'
  }]
}];
function OrderButton({
  label = 'ORDER ONLINE'
}: {
  label?: string;
}) {
  return <a href={ORDER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors" data-id="element-557" data-mp="30pr2st9g5afv">
      {label}
      <ExternalLink size={18} aria-hidden="true" data-id="element-558" data-mp="2auq4mers5clr" />
    </a>;
}
export function Menu() {
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-559" data-mp="2euqv4xs5le96">
      <SEO title="Menu - Grounded Coffee Shop in Livermore, CA" description="Explore the Grounded menu: craft coffee & espresso, made-to-order sandwiches and toasts, pastries, teas, and local goods in Livermore, CA. Order ahead online." canonical="https://www.groundedlivermore.com/menu" data-id="element-560" data-mp="2jpd6j0y8j6t9" />
      <HamburgerMenu data-id="element-561" data-mp="20wsf7ny6p9rk" />

      <main data-id="element-562" data-mp="20w98z4hwc9td">
        {/* Header */}
        <header className="pt-32 pb-16 px-6 md:px-12 lg:px-24 max-w-5xl mx-auto" data-id="element-563" data-mp="20v7n04zce2wv">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-564" data-mp="25wx15thv2wp6">
            <div className="flex items-center gap-3 mb-6" data-id="element-565" data-mp="3ha97cb13frei">
              <div className="h-px w-12 bg-[#D97E3A]" data-id="element-566" data-mp="3hcvcy54fbwu3" />
              <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-xs uppercase" data-id="element-567" data-mp="15mdr5ivh4ots">
                Livermore, CA · Open Daily 7AM – 2PM
              </span>
            </div>

            <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-8" data-id="element-568" data-mp="vwuamjv8h723">
              Grounded Menu — Coffee, Espresso, Made-to-Order Food & Local Goods
              in Livermore
            </h1>

            <p className="font-inter text-lg text-white/70 leading-relaxed max-w-3xl border-l-2 border-[#D97E3A] pl-6" data-id="element-569" data-mp="mysas7n8kkge">
              Grounded is a neighborhood coffee shop in Livermore serving craft
              coffee and espresso, made-to-order sandwiches and toasts, fresh
              pastries, and a shelf of local goods. We brew Proyecto Diaz on
              drip and Chromatic Coffee's Gamut espresso, with rotating
              pour-overs from partner roasters.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-570" data-mp="22ehckkri3wos">
              <OrderButton data-id="element-571" data-mp="26n6eneymwljh" />
              <p className="font-inter text-sm text-white/40 max-w-xs" data-id="element-572" data-mp="26md402wk1bw2">
                Our online menu has live pricing, customization, and pickup
                ordering.
              </p>
            </div>
          </motion.div>
        </header>

        {/* Section index */}
        <nav aria-label="Menu sections" className="px-6 md:px-12 lg:px-24 max-w-5xl mx-auto pb-16" data-id="element-573" data-mp="2bolw6x1ls9x4">
          <ul className="flex flex-wrap gap-x-6 gap-y-3 border-y border-white/10 py-5" data-id="element-574" data-mp="1nvlyxdjlo7nh">
            {menuSections.map(section => <li key={section.id} data-id="element-575" data-mp="1xo1ey5bc6m5q">
                <a href={`#${section.id}`} className="font-bebas text-lg tracking-wide text-white/50 hover:text-[#D97E3A] transition-colors" data-id="element-576" data-mp="1xp2q09scr8ng">
                  {section.title}
                </a>
              </li>)}
          </ul>
        </nav>

        {/* Menu sections */}
        <div className="px-6 md:px-12 lg:px-24 max-w-5xl mx-auto pb-12" data-id="element-577" data-mp="3bvhaq2yzjlb5">
          {menuSections.map(section => <motion.section key={section.id} id={section.id} aria-labelledby={`${section.id}-heading`} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} className="mb-20 scroll-mt-8" data-id="element-578" data-mp="3gmpjqxwel4ns">
              <h2 id={`${section.id}-heading`} className="font-bebas text-4xl md:text-6xl leading-none mb-3" data-id="element-579" data-mp="3g7cx724p296">
                {section.title}
              </h2>

              {section.note ? <p className="font-inter text-sm text-[#D97E3A] mb-8 max-w-2xl" data-id="element-580" data-mp="3plaz6k6kw3kc">
                  {section.note}
                </p> : <div className="mb-8" data-id="element-581" data-mp="2e88izq8e5g0p" />}

              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-7" data-id="element-582" data-mp="2e91q4sb9i7jd">
                {section.items.map(item => <div key={item.name} className="border-t border-white/10 pt-4" data-id="element-583" data-mp="fq6qwbb8kzrv">
                    <dt className="font-inter font-semibold text-white text-base mb-1" data-id="element-584" data-mp="axvqz8ghxdzb">
                      {item.name}
                    </dt>
                    <dd className="font-inter text-sm text-white/60 leading-relaxed" data-id="element-585" data-mp="1yifka8tafby">
                      {item.description}
                    </dd>
                  </div>)}
              </dl>
            </motion.section>)}
        </div>

        {/* Closing CTA */}
        <section className="px-6 md:px-12 lg:px-24 pb-24" data-id="element-586" data-mp="3o7jnogr84map">
          <div className="max-w-5xl mx-auto bg-[#111] border border-white/10 p-10 md:p-14 text-center" data-id="element-587" data-mp="3j7x9a3znt00b">
            <Coffee className="text-[#D97E3A] mx-auto mb-6" size={40} strokeWidth={1.5} aria-hidden="true" data-id="element-588" data-mp="1mdtukwswn7t5" />
            <h2 className="font-bebas text-4xl md:text-5xl mb-4" data-id="element-589" data-mp="1pv83tov7uz60">
              See You Soon
            </h2>
            <p className="font-inter text-lg text-white/70 max-w-xl mx-auto mb-8 leading-relaxed" data-id="element-590" data-mp="1twjxc5h7o4jh">
              Menu rotates seasonally. For today's selection, pricing, and to
              order ahead, visit our online menu.
            </p>
            <OrderButton data-id="element-591" data-mp="23pikdix27n0z" />
          </div>
        </section>

        <VisitUs data-id="element-592" data-mp="16yqdnbw0n7bv" />
        <NewsletterSignup data-id="element-593" data-mp="16z87y2z4bbh5" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-594" data-mp="1az0n4yj0ofvh">
          <p className="font-bebas text-2xl text-white/50" data-id="element-595" data-mp="1b138h5985ktv">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}